import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Box, Popover, List, ListItem, ListItemText } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useAuth from '../../features/hooks/useAuth';
import Sent from '../Assets/Myrequests/Outbox/Sent';
import ConsumSent from '../Consumables/ConsumablesReq/Outbox/ConsumSent';
import CompSentbox from '../Complaints/CompSentbox/CompSentbox';
import { useLazyGetFilteredAssetRequestsQuery } from '../../features/assetrequest/assetReqApiSlice';
import { useLazyGetFilteredConsumableRequestsQuery } from '../../features/consumablerequest/consumableReqApiSlice';
import { useLazyGetFilteredComplaintsLazyQuery } from '../../features/complaints/complaintsApiSlice';

const Container = styled.div`
  padding: 20px 10px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 0 8px 16px 8px;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
  z-index: 1;
`;

const TabButton = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  color: ${props => props.active ? '#1976d2' : '#666'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  border-bottom: ${props => props.active ? '3px solid #1976d2' : '3px solid transparent'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: rgba(25, 118, 210, 0.04);
    color: #1976d2;
  }
`;

const DropdownArrow = styled(ArrowDropDown)`
  font-size: 20px !important;
  transition: transform 0.2s ease;
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
  cursor: pointer;
  
  &:hover {
    background-color: rgba(25, 118, 210, 0.1);
    border-radius: 50%;
  }
`;

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{children}</Box>}
    </div>
  );
};

const UnifiedSentbox = () => {
  const Auth = useAuth();
  const presentOffice = useSelector(state => state.office.off);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Set default main tab based on user type
  const getDefaultMainTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) return parseInt(tabParam, 10);
    return Auth.supportuser ? 4 : 0;
  };
  
  // Set default sub tab based on user type and main tab
  const getDefaultSubTab = (mainTab) => {
    const subParam = searchParams.get('subtab');
    if (subParam !== null) return parseInt(subParam, 10);

    if (mainTab === 4 && Auth.supportuser) {
      const currentOfficeRoles = Auth.roles?.[presentOffice] || [];
      const mainRoles = Auth.roles?.["main"] || [];
      const allUserRoles = [...mainRoles, ...currentOfficeRoles];
      
      const isSupportStaff = allUserRoles.includes("SupportStaff");
      const isSupervisor = allUserRoles.includes("Supervisor");
      
      if (isSupportStaff) {
        return 1;
      } else if (isSupervisor) {
        return 2;
      }
    }
    return 0;
  };

  const defaultMainTab = getDefaultMainTab();
  const [mainTabValue, setMainTabValue] = useState(defaultMainTab);
  const [subTabValue, setSubTabValue] = useState(getDefaultSubTab(defaultMainTab));
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (searchParams.has('tab') || searchParams.has('subtab')) {
      setSearchParams({}, { replace: true });
    }
  }, []);
  const [hasClosedAssetRequests, setHasClosedAssetRequests] = useState(false);
  const [hasClosedConsumableRequests, setHasClosedConsumableRequests] = useState(false);
  const [hasClosedComplaints, setHasClosedComplaints] = useState(false);

  // Role-based boolean variables
  // For support users, Auth.roles is a simple array
  const allUserRoles = Auth.supportuser && Array.isArray(Auth.roles) 
    ? Auth.roles 
    : [...(Auth.roles?.["main"] || []), ...(Auth.roles?.[presentOffice] || [])];  

  
  // Create boolean variables for each role
  const isUser = allUserRoles.includes("user");
  const isItemAdder_A = allUserRoles.includes("ItemAdder_A");
  const isL1Approver_A = allUserRoles.includes("L1Approver_A");
  const isL2Approver_A = allUserRoles.includes("L2Approver_A");
  const isL1ReqApprover_A = allUserRoles.includes("L1ReqApprover_A");
  const isL2ReqApprover_A = allUserRoles.includes("L2ReqApprover_A");
  const isItemAdder_C = allUserRoles.includes("ItemAdder_C");
  const isL1Approver_C = allUserRoles.includes("L1Approver_C");
  const isL2Approver_C = allUserRoles.includes("L2Approver_C");
  const isL1ReqApprover_C = allUserRoles.includes("L1ReqApprover_C");
  const isL2ReqApprover_C = allUserRoles.includes("L2ReqApprover_C");
  const isL1Support = allUserRoles.includes("L1Support");
  const isL2Support = allUserRoles.includes("L2Support");
  const isBo = allUserRoles.includes("Bo");
  const isUnitHead = allUserRoles.includes("UnitHead");
  const isSupportStaff = allUserRoles.includes("SupportStaff");
  const isSupervisor = allUserRoles.includes("Supervisor");

  // API query hooks for checking closed requests
  // These hooks are used to check if the user has any closed requests/complaints
  // to conditionally show the "Closed" sub-tab only when relevant data exists
  const [fetchAssetRequests] = useLazyGetFilteredAssetRequestsQuery();
  const [fetchConsumableRequests] = useLazyGetFilteredConsumableRequestsQuery();
  const [fetchComplaints] = useLazyGetFilteredComplaintsLazyQuery();

  // Function to check for closed asset requests
  const checkClosedAssetRequests = async () => {
    try {
      const result = await fetchAssetRequests({
        page: 1,
        limit: 1, // We only need to check if at least one exists
        filters: JSON.stringify({
          perm_flow_users: [Auth.id],
          perm_curr_status: ['Closed', 'Rejected']
        })
      }).unwrap();
      console.log(result)
      
      setHasClosedAssetRequests(result?.totalFilteredRequests > 0);
    } catch (error) {
      console.error('Error checking closed asset requests:', error);
      setHasClosedAssetRequests(false);
    }
  };

  // Function to check for closed consumable requests
  const checkClosedConsumableRequests = async () => {
    try {
      const result = await fetchConsumableRequests({
        page: 1,
        limit: 1, // We only need to check if at least one exists
        filters: {
          perm_flow_users: [Auth.id],
          perm_curr_status: ['Closed', 'Rejected']
        }
      }).unwrap();
      
      setHasClosedConsumableRequests(result?.totalFilteredRequests > 0);
    } catch (error) {
      console.error('Error checking closed consumable requests:', error);
      setHasClosedConsumableRequests(false);
    }
  };

  // Function to check for closed complaints
  const checkClosedComplaints = async () => {
    try {
      const result = await fetchComplaints({
        page: 1,
        limit: 1, // We only need to check if at least one exists
        filters: JSON.stringify({
          perm_flow_users: [Auth.id],
          perm_curr_status: ['Closed', 'Rejected']
        })
      }).unwrap();
      
      setHasClosedComplaints(result?.totalFilteredComplaints > 0);
    } catch (error) {
      console.error('Error checking closed complaints:', error);
      setHasClosedComplaints(false);
    }
  };

  // Check for closed requests when component mounts or user changes
  useEffect(() => {
    if (Auth?.id) {
      checkClosedAssetRequests();
      checkClosedConsumableRequests();
      checkClosedComplaints();
    }
  }, [Auth?.id]);



  // Handle direct clicks on main tabs
  const handleMainTabClick = (newValue) => {
    console.log("Tab clicked:", newValue);
    setMainTabValue(newValue);
    
    // Re-check for closed requests when switching tabs to ensure accurate visibility
    if (newValue === 0) {
      checkClosedAssetRequests();
    } else if (newValue === 1) {
      checkClosedConsumableRequests();
    } else if (newValue === 4) {
      checkClosedComplaints();
    }
    
    // For support users on complaints tab, set default subtab based on roles
    if (newValue === 4 && Auth.supportuser) {
      // If both roles exist, default to Support User (SupportStaff takes priority)
      if (isSupportStaff) {
        setSubTabValue(1); // Support User Sentbox tab value
      } else if (isSupervisor) {
        setSubTabValue(2); // Support Supervisor Sentbox tab value  
      } else {
        setSubTabValue(0);
      }
    } else {
      // For users with only "user" role, default to User Sent/User tab
      if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
        setSubTabValue(0); // Default to User Sent (value 0) for Asset/Consumable or User (value 0) for Complaints
      } else {
        setSubTabValue(0);
      }
    }
    
    setActiveTab(null);
    setAnchorEl(null);
  };

  const handleSubTabChange = (subTabIndex) => {
    setSubTabValue(subTabIndex);
    setAnchorEl(null);
    setActiveTab(null);
  };

  const handleDropdownClick = (event, tabIndex) => {
    event.stopPropagation(); // Prevent tab click
    
    // Get the tab value from the visible tabs array using the index
    const tabValue = visibleTabs[tabIndex]?.value;
    if (tabValue === undefined) return;
    
    // Check if the tab has sub-tabs
    const visibleSubTabs = getVisibleSubTabs(tabValue);
    if (visibleSubTabs.length <= 1) return;
    
    // Toggle dropdown
    if (activeTab === tabValue && anchorEl) {
      // Close dropdown if clicking the same arrow
      setActiveTab(null);
      setAnchorEl(null);
    } else {
      // Open dropdown for this tab - use the tab element as anchor, not the arrow
      setActiveTab(tabValue);
      setAnchorEl(tabRefs.current[tabIndex]);
    }
  };


  // Filter tabs based on user roles
  const getVisibleTabs = () => {
    let visibleTabs = [];
    
    // If user has only "user" role (no other roles) and is not a support user
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      return [
        { label: 'Asset Request', value: 0 },
        { label: 'Consumable Request', value: 1 },
        { label: 'Complaints', value: 4 },
        { label: 'Notifications', value: 5 }
      ];
    }
    
    // For users with additional roles, build tabs based on their specific roles
    
    // Asset Request tab - show if user has any asset-related roles or basic user access
    if (isUser || isBo || isUnitHead || isL1ReqApprover_A || isL2ReqApprover_A || isItemAdder_A || isL1Approver_A || isL2Approver_A) {
      visibleTabs.push({ label: 'Asset Request', value: 0 });
    }
    
    // Consumable Request tab - show if user has any consumable-related roles or basic user access
    if (isUser || isBo || isUnitHead || isL1ReqApprover_C || isL2ReqApprover_C || isItemAdder_C || isL1Approver_C || isL2Approver_C) {
      visibleTabs.push({ label: 'Consumable Request', value: 1 });
    }
    
    // Add Asset Approval tab - show only if user has asset approval-related roles
    // if (isItemAdder_A || isL1Approver_A || isL2Approver_A) {
    //   visibleTabs.push({ label: 'Add Asset Approval', value: 2 });
    // }
    
    // Add Consumable Approval tab - show only if user has consumable approval-related roles
    // if (isItemAdder_C || isL1Approver_C || isL2Approver_C) {
    //   visibleTabs.push({ label: 'Add Consumable Approval', value: 3 });
    // }
    
    // Complaints tab - show for all users with any role
    visibleTabs.push({ label: 'Complaints', value: 4 });
    
    // Notifications tab - show for all users with any role
    visibleTabs.push({ label: 'Notifications', value: 5 });
    
    return visibleTabs;
  };

  const visibleTabs = getVisibleTabs();

  // Sub-tabs configuration for each main tab
  const subTabs = {
    0: [ // Asset Request sub-tabs
      { label: 'User Sent', value: 0 },
      { label: 'BO Sent', value: 1 },
      { label: 'Unit Head Sent', value: 2 },
      { label: 'Req SentL1', value: 3 },
      { label: 'Req SentL2', value: 4 },
      { label: 'Closed', value:5 },
      { label: 'All Requests', value: 6 }
    ],
    1: [ // Consumable Request sub-tabs
      { label: 'User Sent', value: 0 },
      { label: 'BO Sent', value: 1 },
      { label: 'Unit Head Sent', value: 2 },
      { label: 'Req SentL1', value: 3 },
      { label: 'Req SentL2', value: 4 },
      { label: 'Closed', value:5 },
      { label: 'All Requests', value: 6 }

    ],
    2: [ // Add Asset Approval sub-tabs
      { label: 'Asset Sent', value: 0 },
      { label: 'AssetL1Sent', value: 1 },
      { label: 'AssetL2Sent', value: 2 }
    ],
    3: [ // Add Consumable Approval sub-tabs
      { label: 'Consumable Sent', value: 0 },
      { label: 'ConsumableL1Sent', value: 1 },
      { label: 'ConsumableL2Sent', value: 2 }
    ],
    4: [ // Complaints sub-tabs
      { label: 'User', value: 0 },
      { label: 'Support User', value: 1 },
      { label: 'Support Supervisor', value: 2 },
      { label: 'L1Support', value: 3 },
      { label: 'L2Support', value: 4 },
      { label: 'Closed', value: 5 }
    ],
    5: [ // Notifications sub-tabs
      { label: 'Sent Notifications', value: 0 }
    ]
  };

  // Filter sub-tabs based on user roles
  const getVisibleSubTabs = (tabValue) => {
    const allSubTabs = subTabs[tabValue] || [];
    console.log(hasClosedAssetRequests)
    
    // Check if user has closed requests for the current tab
    let hasClosedRequests = false;
    if (tabValue === 0) { // Asset Request
      hasClosedRequests = hasClosedAssetRequests;
    } else if (tabValue === 1) { // Consumable Request
      hasClosedRequests = hasClosedConsumableRequests;
    } else if (tabValue === 4) { // Complaints
      hasClosedRequests = hasClosedComplaints;
    }
    
    // If user has only "user" role (not support users), show User Sent and optionally Closed
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      let visibleSubTabs = [];
      
      // Always add User Sent for Asset Request and Consumable Request
      if (tabValue === 0 || tabValue === 1) {
        const userSentTab = allSubTabs.find(subTab => subTab.label === 'User Sent');
        if (userSentTab) {
          visibleSubTabs.push(userSentTab);
        }
      }
      
      // For Complaints, add User tab
      if (tabValue === 4) {
        const userTab = allSubTabs.find(subTab => subTab.label === 'User');
        if (userTab) {
          visibleSubTabs.push(userTab);
        }
      }
      
      // Add Closed tab if they have closed requests
      if (hasClosedRequests) {
        const closedTab = allSubTabs.find(subTab => subTab.label === "Closed");
        if (closedTab) {
          visibleSubTabs.push(closedTab);
        }
      }
      
      return visibleSubTabs;
    }
    
    // For users with roles other than just "user", always include User Sent
    let visibleSubTabs = [];
    
    // Always add User Sent for Asset Request and Consumable Request when user has additional roles
    if (tabValue === 0 || tabValue === 1) { // Asset Request or Consumable Request
      const userSentTab = allSubTabs.find(subTab => subTab.label === 'User Sent');
      if (userSentTab) {
        visibleSubTabs.push(userSentTab);
      }
    }
    
    // Add role-specific tabs for Asset Request and Consumable Request
    if (tabValue === 0 || tabValue === 1) { // Asset Request or Consumable Request
      
      // If user has Bo role, add BO Sent
      if (isBo) {
        const boSentTab = allSubTabs.find(subTab => subTab.label === 'BO Sent');
        if (boSentTab) {
          visibleSubTabs.push(boSentTab);
        }
      }
      
      // If user has UnitHead role, add Unit Head Sent
      if (isUnitHead) {
        const unitHeadSentTab = allSubTabs.find(subTab => subTab.label === 'Unit Head Sent');
        if (unitHeadSentTab) {
          visibleSubTabs.push(unitHeadSentTab);
        }
      }
      
      // If user has L1ReqApprover_A role and it's Asset Request, add Req SentL1
      if (isL1ReqApprover_A && tabValue === 0) {
        const l1SentTab = allSubTabs.find(subTab => subTab.label === 'Req SentL1');
        if (l1SentTab) {
          visibleSubTabs.push(l1SentTab);
        }
      }
      
      // If user has L2ReqApprover_A role and it's Asset Request, add Req SentL2
      if (isL2ReqApprover_A && tabValue === 0) {
        const l2SentTab = allSubTabs.find(subTab => subTab.label === 'Req SentL2');
        if (l2SentTab) {
          visibleSubTabs.push(l2SentTab);
        }
      }
      
      // If user has L1ReqApprover_C role and it's Consumable Request, add Req SentL1
      if (isL1ReqApprover_C && tabValue === 1) {
        const l1SentTab = allSubTabs.find(subTab => subTab.label === 'Req SentL1');
        if (l1SentTab) {
          visibleSubTabs.push(l1SentTab);
        }
      }
      
      // If user has L2ReqApprover_C role and it's Consumable Request, add Req SentL2
      if (isL2ReqApprover_C && tabValue === 1) {
        const l2SentTab = allSubTabs.find(subTab => subTab.label === 'Req SentL2');
        if (l2SentTab) {
          visibleSubTabs.push(l2SentTab);
        }
      }

      // Add "All Requests" tab for Asset Request (tabValue === 0)
      if (tabValue === 0) {
        // Show "All Requests" for L1ReqApprover_A, L2ReqApprover_A, or UnitHead of store office
        if (isL1ReqApprover_A || isL2ReqApprover_A || (isUnitHead && Auth.store)) {
          const allRequestsTab = allSubTabs.find(subTab => subTab.label === 'All Requests');
          if (allRequestsTab) {
            visibleSubTabs.push(allRequestsTab);
          }
        }
      }
      
      // Add "All Requests" tab for Consumable Request (tabValue === 1) 
      if (tabValue === 1) {
        // Show "All Requests" for L1ReqApprover_C, L2ReqApprover_C, or UnitHead of store office
        if (isL1ReqApprover_C || isL2ReqApprover_C || (isUnitHead && Auth.store)) {
          const allRequestsTab = allSubTabs.find(subTab => subTab.label === 'All Requests');
          if (allRequestsTab) {
            visibleSubTabs.push(allRequestsTab);
          }
        }
      }

      // Add Closed tab if user has closed requests (only for users with additional roles)
      if (!(allUserRoles.length === 1 && isUser && !Auth.supportuser)) {
        const hasClosedRequests = tabValue === 0 ? hasClosedAssetRequests : hasClosedConsumableRequests;
        if (hasClosedRequests) {
          const closedTab = allSubTabs.find(subTab => subTab.label === "Closed");
          if (closedTab) {
            visibleSubTabs.push(closedTab);
          }
        }
      }
    }
    // Handle Complaints tab (tabValue === 4)
    if (tabValue === 4) {
      // Check if user is a support user
      if (Auth.supportuser) {
        // For support users, only show Support User and Support Supervisor sentbox subtabs
        if (isSupportStaff) {
          const supportUserSentboxTab = allSubTabs.find(subTab => subTab.label === 'Support User');
          if (supportUserSentboxTab) {
            visibleSubTabs.push(supportUserSentboxTab);
          }
        }

        if (isSupervisor) {
          const supportSupervisorSentboxTab = allSubTabs.find(subTab => subTab.label === 'Support Supervisor');
          if (supportSupervisorSentboxTab) {
            visibleSubTabs.push(supportSupervisorSentboxTab);
          }
        }
      } else {
        // For normal users, keep the existing logic
        // Always add User Sentbox for users with any role
        const userSentboxTab = allSubTabs.find(subTab => subTab.label === 'User');
        if (userSentboxTab) {
          visibleSubTabs.push(userSentboxTab);
        }        

        
        // If user has L1Support role, add L1Support Sentbox
        if (isL1Support) {
          const l1SupportSentboxTab = allSubTabs.find(subTab => subTab.label === 'L1Support');
          if (l1SupportSentboxTab) {
            visibleSubTabs.push(l1SupportSentboxTab);
          }
        }

        // If user has L2Support role, add L2Support Sentbox
        if (isL2Support) {
          const l2SupportSentboxTab = allSubTabs.find(subTab => subTab.label === 'L2Support');
          if (l2SupportSentboxTab) {
            visibleSubTabs.push(l2SupportSentboxTab);
          }
        }
      }

      // Add Closed tab for complaints if user has closed complaints (only for users with additional roles)
      if (!(allUserRoles.length === 1 && isUser && !Auth.supportuser)) {
        if (hasClosedComplaints) {
          const closedTab = allSubTabs.find(subTab => subTab.label === 'Closed');
          if (closedTab) {
            visibleSubTabs.push(closedTab);
          }
        }
      }
    }
    
    // Handle Notifications tab (tabValue === 5) - return default sub-tabs
    if (tabValue === 5) {
      return allSubTabs;
    }
    
    // Handle Add Asset Approval tab (tabValue === 2)
    if (tabValue === 2) {
      // If user has ItemAdder_A role, add Asset Sent
      if (isItemAdder_A) {
        const assetSentTab = allSubTabs.find(subTab => subTab.label === 'Asset Sent');
        if (assetSentTab) {
          visibleSubTabs.push(assetSentTab);
        }
      }
      
      // If user has L1Approver_A role, add AssetL1Sent
      if (isL1Approver_A) {
        const l1SentTab = allSubTabs.find(subTab => subTab.label === 'AssetL1Sent');
        if (l1SentTab) {
          visibleSubTabs.push(l1SentTab);
        }
      }
      
      // If user has L2Approver_A role, add AssetL2Sent
      if (isL2Approver_A) {
        const l2SentTab = allSubTabs.find(subTab => subTab.label === 'AssetL2Sent');
        if (l2SentTab) {
          visibleSubTabs.push(l2SentTab);
        }
      }
    }
    
    // Handle Add Consumable Approval tab (tabValue === 3)
    if (tabValue === 3) {
      // If user has ItemAdder_C role, add Consumable Sent
      if (isItemAdder_C) {
        const consumableSentTab = allSubTabs.find(subTab => subTab.label === 'Consumable Sent');
        if (consumableSentTab) {
          visibleSubTabs.push(consumableSentTab);
        }
      }
      
      // If user has L1Approver_C role, add ConsumableL1Sent
      if (isL1Approver_C) {
        const l1SentTab = allSubTabs.find(subTab => subTab.label === 'ConsumableL1Sent');
        if (l1SentTab) {
          visibleSubTabs.push(l1SentTab);
        }
      }
      
      // If user has L2Approver_C role, add ConsumableL2Sent
      if (isL2Approver_C) {
        const l2SentTab = allSubTabs.find(subTab => subTab.label === 'ConsumableL2Sent');
        if (l2SentTab) {
          visibleSubTabs.push(l2SentTab);
        }
      }
    }
    
    // Remove duplicates and return
    return visibleSubTabs.filter((subTab, index, self) => 
      index === self.findIndex(t => t.value === subTab.value)
    );
  };

  // Initialize refs array for tabs
  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, visibleTabs.length);
  }, [visibleTabs.length]);

  // Initialize correct tab values for support users on mount
  useEffect(() => {
    if (Auth.supportuser && Auth.roles && Array.isArray(Auth.roles)) {
      const isSupportStaff = Auth.roles.includes("SupportStaff");
      const isSupervisor = Auth.roles.includes("Supervisor");
      
      // Set Complaints as default main tab
      if (mainTabValue !== 4) {
        setMainTabValue(4);
      }
      
      // Set appropriate subtab based on roles
      let correctSubTabValue = 0;
      if (isSupportStaff) {
        correctSubTabValue = 1; // Support User Sentbox tab
      } else if (isSupervisor) {
        correctSubTabValue = 2; // Support Supervisor Sentbox tab
      }
      
      if (subTabValue !== correctSubTabValue) {
        setSubTabValue(correctSubTabValue);
      }
    }
  }, [Auth.supportuser, Auth.roles, mainTabValue, subTabValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOpen = Boolean(anchorEl) && activeTab !== null;
      if (isOpen && !event.target.closest('.MuiPopover-root') && !event.target.closest('[data-dropdown-arrow]')) {
        setActiveTab(null);
        setAnchorEl(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl, activeTab]);


  const getTabLabel = (tab) => {
    // For users with only "user" role (not support users), only show sub-tab labels if they have closed requests
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      const visibleSubTabs = getVisibleSubTabs(tab.value);
      if (visibleSubTabs.length === 0) {
        return tab.label;
      }
    }
    
    if (mainTabValue === tab.value) {
      const visibleSubTabs = getVisibleSubTabs(tab.value);
      // Find the sub-tab by its value property instead of using index
      const selectedSubTab = visibleSubTabs.find(subTab => subTab.value === subTabValue);
      if (selectedSubTab) {
        return `${tab.label} (${selectedSubTab.label})`;
      }
    }
    return tab.label;
  };

  const open = Boolean(anchorEl) && activeTab !== null;

  return (
    <Container>
      {/* Main Tabs */}
      <TabsContainer>
        {visibleTabs.map((tab, index) => (
          <TabButton
            key={tab.value}
            ref={el => tabRefs.current[index] = el}
            active={mainTabValue === tab.value}
            onClick={() => handleMainTabClick(tab.value)}
          >
            <span>{getTabLabel(tab)}</span>
            {(() => {
              const visibleSubTabs = getVisibleSubTabs(tab.value);
              const hasDropdown = visibleSubTabs.length > 1;
              
              return hasDropdown ? (
                <DropdownArrow 
                  open={activeTab === tab.value && Boolean(anchorEl)}
                  onClick={(e) => handleDropdownClick(e, index)}
                  data-dropdown-arrow="true"
                />
              ) : null;
            })()}
          </TabButton>
        ))}
      </TabsContainer>

      {/* Sub-tabs Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setActiveTab(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{ 
          pointerEvents: 'auto',
          mt: 0.5,
          '& .MuiPopover-paper': {
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
          }
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        <List 
          dense 
          sx={{ 
            p: 0, 
            '& .MuiListItem-root': {
              borderBottom: '1px solid #f0f0f0',
              padding: '12px 16px',
              '&:last-child': {
                borderBottom: 'none'
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            } 
          }}
        >
          {activeTab !== null && getVisibleSubTabs(activeTab).map((subTab) => (
            <ListItem 
              key={subTab.value} 
              button
              selected={mainTabValue === activeTab && subTabValue === subTab.value}
              onClick={() => {
                if (mainTabValue !== activeTab) {
                  setMainTabValue(activeTab);
                }
                handleSubTabChange(subTab.value);
              }}
              sx={{ 
                py: 1.5,
                backgroundColor: (mainTabValue === activeTab && subTabValue === subTab.value) 
                  ? 'rgba(25, 118, 210, 0.08)' 
                  : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)'
                }
              }}
            >
              <ListItemText 
                primary={subTab.label} 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: (mainTabValue === activeTab && subTabValue === subTab.value) ? 600 : 400
                }} 
              />
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* Tab Panels */}
      <TabPanel value={mainTabValue} index={0}>
        {/* Asset Request Tab Content */}
        <Box sx={{ mx: 1 }}>
          <Sent subTabValue={subTabValue} />
        </Box>
      </TabPanel>

      <TabPanel value={mainTabValue} index={1}>
        {/* Consumable Request Tab Content */}
        <Box sx={{ mx: 1 }}>
          <ConsumSent subTabValue={subTabValue} />
        </Box>
      </TabPanel>

      <TabPanel value={mainTabValue} index={2}>
        {/* Add Asset Approval Tab Content */}
        <Box sx={{ mx: 1 }}>
          <div>Add Asset Approval Sentbox content will go here</div>
        </Box>
      </TabPanel>

      <TabPanel value={mainTabValue} index={3}>
        {/* Add Consumable Approval Tab Content */}
        <Box sx={{ mx: 1 }}>
          <div>Add Consumable Approval Sentbox content will go here</div>
        </Box>
      </TabPanel>

      <TabPanel value={mainTabValue} index={4}>
        {/* Complaints Tab Content */}
        <Box sx={{ mx: 1 }}>
          <CompSentbox subTabValue={subTabValue} />
        </Box>
      </TabPanel>

      <TabPanel value={mainTabValue} index={5}>
        {/* Notifications Tab Content */}
        <Box sx={{ mx: 1 }}>
          <div>Notifications Sentbox content will go here</div>
        </Box>
      </TabPanel>
    </Container>
  );
};

export default UnifiedSentbox;